/** Mirrors public/includes/shipping_helpers.php for live checkout quotes. */

const REMOTE_STATES = new Set([
  'arunachal pradesh',
  'assam',
  'manipur',
  'meghalaya',
  'mizoram',
  'nagaland',
  'tripura',
  'sikkim',
  'jammu and kashmir',
  'jammu & kashmir',
  'ladakh',
  'himachal pradesh',
  'uttarakhand',
  'andaman and nicobar islands',
  'andaman & nicobar islands',
  'lakshadweep',
]);

const METRO_STATES = new Set([
  'maharashtra',
  'delhi',
  'nct of delhi',
  'national capital territory of delhi',
  'karnataka',
  'tamil nadu',
  'telangana',
  'west bengal',
  'haryana',
  'uttar pradesh',
  'rajasthan',
  'punjab',
  'madhya pradesh',
  'andhra pradesh',
  'kerala',
  'odisha',
  'orissa',
  'bihar',
  'jharkhand',
  'chhattisgarh',
  'goa',
]);

const STATE_ALIASES = {
  orissa: 'odisha',
  pondicherry: 'puducherry',
  'nct of delhi': 'delhi',
  'national capital territory of delhi': 'delhi',
  'jammu & kashmir': 'jammu and kashmir',
  'andaman & nicobar islands': 'andaman and nicobar islands',
};

function normalizeStateKey(state) {
  let s = String(state || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  if (STATE_ALIASES[s]) s = STATE_ALIASES[s];
  return s;
}

function localStateKey(rules) {
  const name = String(rules?.local_state || 'Gujarat').trim();
  return normalizeStateKey(name || 'Gujarat');
}

function isLocalState(stateKey, rules) {
  if (!stateKey) return false;
  const local = localStateKey(rules);
  return local !== '' && stateKey === local;
}

function localZoneLabel(rules) {
  const name = String(rules?.local_state || 'Gujarat').trim() || 'Gujarat';
  return `${name} delivery`;
}

/**
 * @param {object} rules from checkout_config shipping_rules
 * @param {number} subtotal
 * @param {number} discount
 * @param {string} state
 * @param {string} pincode
 */
export function computeShipping(rules, subtotal, discount, state, pincode) {
  const r = rules || {};
  const merchandise = Math.max(0, Number(subtotal) - Number(discount || 0));
  const minOrder = Number(r.min_order_inr) || 0;
  if (minOrder > 0 && merchandise > 0 && merchandise < minOrder) {
    return {
      ok: false,
      error: `Minimum order value is ₹${Math.round(minOrder).toLocaleString('en-IN')} before delivery.`,
    };
  }

  const pin = String(pincode || '').replace(/\D/g, '');
  const freeAbove = Number(r.free_above_inr) || 0;
  if (freeAbove > 0 && merchandise >= freeAbove) {
    return {
      ok: true,
      fee_inr: 0,
      zone: 'free',
      label: 'Free delivery',
      free: true,
      merchandise,
    };
  }

  if (r.mode !== 'zones') {
    const fee = Math.max(0, Number(r.flat_inr) || 0);
    return {
      ok: true,
      fee_inr: fee,
      zone: 'flat',
      label: fee > 0 ? 'Standard delivery' : 'Free delivery',
      free: fee <= 0,
      merchandise,
    };
  }

  const stateKey = normalizeStateKey(state);
  if (!stateKey && pin.length !== 6) {
    const fee = Math.max(0, Number(r.flat_inr) || 0);
    return {
      ok: true,
      fee_inr: fee,
      zone: 'india',
      label: 'All India delivery',
      free: false,
      merchandise,
      estimate: true,
    };
  }

  let zone = 'india';
  let label = 'All India delivery';
  let fee = Number(r.flat_inr) || 0;

  const localFee = Number(r.local_inr ?? r.gujarat_inr);
  if (stateKey && isLocalState(stateKey, r)) {
    zone = 'local';
    label = localZoneLabel(r);
    fee = Number.isFinite(localFee) ? localFee : fee;
  } else if (stateKey && REMOTE_STATES.has(stateKey)) {
    zone = 'remote';
    label = 'Remote / NE & hills';
    fee = Number(r.remote_inr) ?? fee;
  } else if (stateKey && METRO_STATES.has(stateKey)) {
    zone = 'metro';
    label = 'Metro & major states';
    fee = Number(r.metro_inr) ?? fee;
  }

  return {
    ok: true,
    fee_inr: Math.max(0, fee),
    zone,
    label,
    free: fee <= 0,
    merchandise,
  };
}

/** Customer-facing delivery note under order summary. */
export function shippingDeliveryNote(quote, rules) {
  if (!quote?.ok) return quote?.error || '';
  const r = rules || {};
  if (quote.free && quote.zone === 'free') {
    const above = Number(r.free_above_inr) || 0;
    if (above > 0) {
      return `Free delivery applied — your order is above ₹${Math.round(above).toLocaleString('en-IN')}.`;
    }
    return 'Complimentary delivery on this order.';
  }
  if (quote.estimate) {
    return 'Enter PIN and state for your exact delivery fee.';
  }
  if (quote.fee_inr > 0) {
    const parts = [quote.label || 'Delivery'];
    const above = Number(r.free_above_inr) || 0;
    if (above > 0) {
      const left = above - (quote.merchandise || 0);
      if (left > 0) {
        parts.push(`Free delivery on orders over ₹${Math.round(above).toLocaleString('en-IN')} (add ₹${Math.round(left).toLocaleString('en-IN')} more).`);
      }
    }
    return parts.join('. ');
  }
  return 'Complimentary delivery across India on this order.';
}
